<template>
  <div class="modal fade show" style="display: block; background: #1c1d1c33">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content cancel-preview-modal">
        <div class="d-flex justify-content-between align-items-center px-2 py-1">
          <h4 class="mb-0">Cancel Incentive Payment Voucher</h4>
          <button @click="onClose" type="button" class="btn-close" aria-label="Close"></button>
        </div>

        <hr class="m-0">

        <div class="modal-body">
          <div class="row mb-1">
            <div class="col-md-3 col-6 mb-1">
              <small class="d-block">Voucher No</small>
              <span class="fw-bold">{{ ledger.voucher_no }}</span>
            </div>
            <div class="col-md-3 col-6 mb-1">
              <small class="d-block">Voucher Date</small>
              <span class="fw-bold">{{ ledger.date }}</span>
            </div>
            <div class="col-md-3 col-6 mb-1">
              <small class="d-block">Staff</small>
              <span class="fw-bold">{{ ledger.full_name }}<span v-if="ledger.serial_no"> ({{ ledger.serial_no }})</span></span>
            </div>
            <div v-if="voucherFormData.month" class="col-md-3 col-6 mb-1">
              <small class="d-block">Month</small>
              <span class="fw-bold">{{ voucherFormData.month }}</span>
            </div>
            <div v-if="voucherFormData.mop_account_head_name" class="col-md-3 col-6 mb-1">
              <small class="d-block">Paid From</small>
              <span class="fw-bold">{{ voucherFormData.mop_account_head_name }}</span>
            </div>
            <div v-if="voucherFormData.payment_account_head_name" class="col-md-3 col-6 mb-1">
              <small class="d-block">Payment Purpose</small>
              <span class="fw-bold">{{ voucherFormData.payment_account_head_name }}</span>
            </div>
            <div class="col-md-3 col-6 mb-1">
              <small class="d-block">Paid Amount</small>
              <span class="fw-bold">{{ commaFormat(ledger.total_amount) }}</span>
            </div>
            <div class="col-md-3 col-6 mb-1">
              <small class="d-block">Remarks</small>
              <span class="fw-bold">{{ ledger.note }}</span>
            </div>
          </div>

          <div v-if="!rows.length" class="alert alert-warning p-1 mb-0">
            No group sale commission or company sale share is linked to this voucher.
          </div>

          <div v-if="rows.length" class="table-responsive">
            <table class="table table-bordered custom-table-padding">
              <thead>
              <tr>
                <th>Staff ID</th>
                <th>Staff Name</th>
                <th>Designation</th>
                <th>Ref ID</th>
                <th class="text-end">Personal Sales Commission</th>
                <th class="text-end">Group Sales Commission</th>
                <th class="text-end">Company Sales Share</th>
                <th class="text-end">BP Commission</th>
                <th class="text-end">Total Commission</th>
                <th class="text-end">Service Charge</th>
                <th class="text-end">Net Commission</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, index) in rows" :key="index">
                <td>{{ item.serial_no }}</td>
                <td>{{ item.name }}</td>
                <td>{{ item.level_name }}({{ item.equivalent_designation }})</td>
                <td>{{ item.parent_serial_no }}</td>
                <td class="text-end">{{ commaFormat(item.personal_sales_commission) }}</td>
                <td class="text-end">{{ commaFormat(item.total_group_commission_amount) }}</td>
                <td class="text-end">{{ commaFormat(item.total_company_sale_share) }}</td>
                <td class="text-end">{{ commaFormat(item.total_group_bp_commission_amount) }}</td>
                <td class="text-end">{{ commaFormat(item.total_commission) }}</td>
                <td class="text-end">{{ commaFormat(item.service_charge) }}</td>
                <td class="text-end">{{ commaFormat(item.net_commission) }}</td>
              </tr>
              <tr class="fw-bold">
                <td colspan="4" class="text-end">Total</td>
                <td class="text-end">{{ commaFormat(total.total_personal_sales_commission) }}</td>
                <td class="text-end">{{ commaFormat(total.total_group_commission_amount) }}</td>
                <td class="text-end">{{ commaFormat(total.total_company_sale_share) }}</td>
                <td class="text-end">{{ commaFormat(total.total_group_bp_commission_amount) }}</td>
                <td class="text-end">{{ commaFormat(total.total_commission) }}</td>
                <td class="text-end">{{ commaFormat(total.service_charge) }}</td>
                <td class="text-end">{{ commaFormat(total.net_commission) }}</td>
              </tr>
              </tbody>
            </table>
          </div>

          <div class="mt-2">
            <label class="form-label" for="cancelNote"><span class="required">*</span> Cancel Reason</label>
            <textarea
                id="cancelNote"
                v-model="note"
                class="form-control"
                rows="2"
                placeholder="Enter cancel reason"
            ></textarea>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="onClose" :disabled="loading" type="button" class="btn btn-secondary">
            Close
          </button>
          <button @click="onConfirm" :disabled="!canConfirm" type="button" class="btn btn-danger">
            <span v-if="loading" class="spinner-border spinner-border-sm me-50" role="status"></span>
            Cancel Voucher
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import figureFormatter from '@/services/utils/figureFormatter'

const props = defineProps({
  voucher: { type: Object, required: true },
  loading: { type: Boolean, default: false }
})

const emit = defineEmits(['close', 'confirm'])

const { commaFormat } = figureFormatter()

const note = ref('')

const ledger = computed(() => props.voucher.data || {})
const voucherFormData = computed(() => props.voucher.form_data || {})
const rows = computed(() => props.voucher.incentive_payment || [])
const total = computed(() => props.voucher.total || {})
const canConfirm = computed(() => note.value.trim().length > 0 && !props.loading)

const onClose = () => {
  if (props.loading) return
  emit('close')
}

const onConfirm = () => {
  if (!canConfirm.value) return
  emit('confirm', note.value.trim())
}
</script>

<style scoped>
.cancel-preview-modal,
.cancel-preview-modal h4,
.cancel-preview-modal small,
.cancel-preview-modal label,
.cancel-preview-modal textarea,
.cancel-preview-modal .table th,
.cancel-preview-modal .table td {
  color: #000;
}
</style>
